import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Play, BookOpen, Settings, TrendingUp, Globe, Smartphone, CreditCard, HelpCircle, CheckCircle } from 'lucide-react';

const UserManualPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);

  const sections = [
    {
      id: 'getting-started',
      title: 'Primeiros Passos',
      icon: <BookOpen className="h-5 w-5" />,
      subsections: [
        { id: 'overview', title: 'Visão Geral do Sistema' },
        { id: 'quick-setup', title: 'Configuração Rápida (15 min)' },
        { id: 'first-commission', title: 'Sua Primeira Comissão' }
      ]
    },
    {
      id: 'api-setup',
      title: 'Configuração de APIs',
      icon: <Settings className="h-5 w-5" />,
      subsections: [
        { id: 'clickbank-setup', title: 'ClickBank API' },
        { id: 'hotmart-setup', title: 'Hotmart API' },
        { id: 'monetizze-setup', title: 'Monetizze API' },
        { id: 'amazon-setup', title: 'Amazon Associates' }
      ]
    },
    {
      id: 'content-generation',
      title: 'Geração de Conteúdo',
      icon: <Smartphone className="h-5 w-5" />,
      subsections: [
        { id: 'ai-content', title: 'IA de Conteúdo' },
        { id: 'social-posts', title: 'Posts para Redes Sociais' },
        { id: 'email-campaigns', title: 'Campanhas de Email' },
        { id: 'landing-pages', title: 'Landing Pages' }
      ]
    },
    {
      id: 'tracking',
      title: 'Tracking e Analytics',
      icon: <TrendingUp className="h-5 w-5" />,
      subsections: [
        { id: 'dashboard', title: 'Dashboard em Tempo Real' },
        { id: 'conversion-tracking', title: 'Tracking de Conversões' },
        { id: 'performance-metrics', title: 'Métricas de Performance' }
      ]
    },
    {
      id: 'automation',
      title: 'Automação Social',
      icon: <Globe className="h-5 w-5" />,
      subsections: [
        { id: 'social-connect', title: 'Conectar Redes Sociais' },
        { id: 'auto-posting', title: 'Postagem Automática' },
        { id: 'engagement', title: 'Engajamento Automático' }
      ]
    },
    {
      id: 'payments',
      title: 'Sistema de Pagamentos',
      icon: <CreditCard className="h-5 w-5" />,
      subsections: [
        { id: 'payment-setup', title: 'Configurar Pagamentos' },
        { id: 'pix-setup', title: 'PIX (Brasil)' },
        { id: 'stripe-setup', title: 'Stripe (Internacional)' },
        { id: 'paypal-setup', title: 'PayPal (Global)' }
      ]
    }
  ];

  const faqs = [
    {
      question: "Quanto tempo leva para ver os primeiros resultados?",
      answer: "Com o sistema configurado corretamente, você pode ver os primeiros cliques em 24-48 horas e as primeiras vendas entre 7-15 dias, dependendo do seu nicho e estratégia de tráfego."
    },
    {
      question: "Preciso ter conhecimento técnico?",
      answer: "Não! O sistema foi desenvolvido para ser usado por qualquer pessoa. Toda a configuração é feita através de interfaces visuais simples, sem necessidade de programação."
    },
    {
      question: "Quanto posso ganhar por mês?",
      answer: "Os ganhos variam conforme seu empenho e nicho. Nossos usuários reportam desde R$ 1.000/mês (iniciantes) até R$ 50.000+/mês (usuários avançados)."
    },
    {
      question: "As integrações são reais ou simuladas?",
      answer: "Todas as integrações são 100% reais com as APIs oficiais das plataformas. Você conecta suas contas reais e recebe comissões diretamente delas."
    },
    {
      question: "O sistema funciona fora do Brasil?",
      answer: "Sim! O sistema opera em 6 países (Brasil, EUA, Canadá, Reino Unido, Alemanha, França) com suporte a múltiplas moedas e idiomas."
    }
  ];

  const toggleStep = (stepId) => {
    if (completedSteps.includes(stepId)) {
      setCompletedSteps(completedSteps.filter(id => id !== stepId));
    } else {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">🚀 Primeiros Passos</h2>
              <p className="text-gray-300 text-lg mb-8">
                Bem-vindo ao MonetizeMaster! Este guia te levará do zero aos primeiros R$ 1.000 em comissões.
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-4">✅ Checklist de Configuração Inicial</h3>
              <div className="space-y-4">
                {[
                  { id: 'step1', text: 'Configurar APIs das 4 plataformas (ClickBank, Hotmart, Monetizze, Amazon)' },
                  { id: 'step2', text: 'Gerar seus primeiros 5 links de afiliado' },
                  { id: 'step3', text: 'Criar conteúdo com IA para pelo menos 3 produtos' },
                  { id: 'step4', text: 'Conectar pelo menos 2 redes sociais' },
                  { id: 'step5', text: 'Configurar método de pagamento preferido' },
                  { id: 'step6', text: 'Publicar primeiro conteúdo nas redes sociais' }
                ].map((step) => (
                  <div key={step.id} className="flex items-center gap-3">
                    <button
                      onClick={() => toggleStep(step.id)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                        completedSteps.includes(step.id)
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-400 hover:border-green-400'
                      }`}
                    >
                      {completedSteps.includes(step.id) && <CheckCircle className="h-4 w-4" />}
                    </button>
                    <span className={`${completedSteps.includes(step.id) ? 'text-green-400 line-through' : 'text-gray-300'}`}>
                      {step.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">📊 Expectativas Realistas</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• <strong>Semana 1:</strong> Configuração e primeiros cliques</li>
                  <li>• <strong>Semana 2-3:</strong> Primeira venda (R$ 50-200)</li>
                  <li>• <strong>Mês 2:</strong> R$ 500-1.500/mês</li>
                  <li>• <strong>Mês 3+:</strong> R$ 2.000-5.000+/mês</li>
                </ul>
              </div>
              
              <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">🎯 Dicas de Ouro</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Foque em produtos de R$ 200-2000</li>
                  <li>• Use a IA para criar 3-5 posts por dia</li>
                  <li>• Monitore o dashboard toda manhã</li>
                  <li>• Teste produtos diferentes semanalmente</li>
                  <li>• Responda comentários (aumenta conversão)</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'api-setup':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">⚙️ Configuração de APIs</h2>
              <p className="text-gray-300 text-lg mb-8">
                Configure suas chaves de API para conectar com as 4 principais plataformas de afiliados.
              </p>
            </div>

            <div className="grid gap-6">
              {/* ClickBank */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-400 mb-4">🏦 ClickBank API</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Como Obter:</h4>
                    <ol className="text-gray-300 space-y-1 list-decimal list-inside">
                      <li>Acesse <a href="https://accounts.clickbank.com" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">accounts.clickbank.com</a></li>
                      <li>Faça login na sua conta ClickBank</li>
                      <li>Vá em "Settings" → "My Site"</li>
                      <li>Copie sua "API Key" e "Developer Key"</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Campos Necessários:</h4>
                    <ul className="text-gray-300 space-y-1">
                      <li>• <strong>API Key:</strong> Obrigatório para acessar a API</li>
                      <li>• <strong>Developer Key:</strong> Opcional, para funcionalidades avançadas</li>
                    </ul>
                  </div>
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-4">
                    <p className="text-green-400 font-semibold">💰 Potencial: R$ 150-500 por venda | Cookie: 60 dias</p>
                  </div>
                </div>
              </div>

              {/* Hotmart */}
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-orange-400 mb-4">🔥 Hotmart API</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Como Obter:</h4>
                    <ol className="text-gray-300 space-y-1 list-decimal list-inside">
                      <li>Acesse <a href="https://developers.hotmart.com" className="text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer">developers.hotmart.com</a></li>
                      <li>Faça login com sua conta Hotmart</li>
                      <li>Vá em "Minhas Aplicações" → "Nova Aplicação"</li>
                      <li>Copie o "Client ID" e "Client Secret"</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Campos Necessários:</h4>
                    <ul className="text-gray-300 space-y-1">
                      <li>• <strong>Client ID:</strong> Obrigatório</li>
                      <li>• <strong>Client Secret:</strong> Obrigatório</li>
                    </ul>
                  </div>
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-4">
                    <p className="text-green-400 font-semibold">💰 Potencial: 20-70% de comissão | PIX semanal automático</p>
                  </div>
                </div>
              </div>

              {/* Monetizze */}
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-purple-400 mb-4">💎 Monetizze API</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Como Obter:</h4>
                    <ol className="text-gray-300 space-y-1 list-decimal list-inside">
                      <li>Acesse <a href="https://app.monetizze.com.br" className="text-purple-400 hover:underline" target="_blank" rel="noopener noreferrer">app.monetizze.com.br</a></li>
                      <li>Faça login na sua conta</li>
                      <li>Vá em "Integrações" → "API"</li>
                      <li>Copie seu "Token de API"</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Campos Necessários:</h4>
                    <ul className="text-gray-300 space-y-1">
                      <li>• <strong>Token/Chave de API:</strong> Apenas 1 campo necessário</li>
                    </ul>
                  </div>
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-4">
                    <p className="text-green-400 font-semibold">💰 Potencial: 30-80% de comissão | Foco em infoprodutos</p>
                  </div>
                </div>
              </div>

              {/* Amazon */}
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">📦 Amazon Associates</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Como Obter:</h4>
                    <ol className="text-gray-300 space-y-1 list-decimal list-inside">
                      <li>Acesse <a href="https://affiliate-program.amazon.com" className="text-yellow-400 hover:underline" target="_blank" rel="noopener noreferrer">affiliate-program.amazon.com</a></li>
                      <li>Cadastre-se no programa</li>
                      <li>Acesse "Ferramentas" → "Product Advertising API"</li>
                      <li>Gere suas credenciais de acesso</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Campos Necessários:</h4>
                    <ul className="text-gray-300 space-y-1">
                      <li>• <strong>Access Key:</strong> Obrigatório</li>
                      <li>• <strong>Secret Key:</strong> Obrigatório</li>
                      <li>• <strong>Associate Tag:</strong> Obrigatório</li>
                      <li>• <strong>Região:</strong> US, BR, CA, UK, DE, FR</li>
                    </ul>
                  </div>
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-4">
                    <p className="text-green-400 font-semibold">💰 Potencial: 1-10% (alto volume) | Conversão muito alta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">📚 Manual do Usuário</h2>
              <p className="text-gray-300 text-lg">
                Selecione uma seção no menu lateral para ver o conteúdo detalhado.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            📚 Manual do Usuário
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent ml-4">
              MonetizeMaster
            </span>
          </h1>
          <p className="text-gray-300 text-lg">
            Guia completo para dominar o sistema e maximizar seus lucros
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-6 sticky top-8">
              <h3 className="text-lg font-bold text-white mb-4">Índice</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <div key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                        activeSection === section.id
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {section.icon}
                      <span className="font-medium">{section.title}</span>
                    </button>
                    {activeSection === section.id && section.subsections && (
                      <div className="ml-8 mt-2 space-y-1">
                        {section.subsections.map((sub) => (
                          <button
                            key={sub.id}
                            className="block w-full text-left text-sm text-gray-400 hover:text-yellow-400 py-1"
                          >
                            {sub.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-8">
              {renderContent()}
            </div>

            {/* FAQ Section */}
            <div className="mt-8 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <HelpCircle className="h-6 w-6 text-yellow-400" />
                Perguntas Frequentes
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-white/10 rounded-lg">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="font-medium text-white">{faq.question}</span>
                      {expandedFaq === index ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-300">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManualPage;